import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { Edit, Plus, Search, UserCheck, UserX } from 'lucide-react';
import { ERPUser } from '../../core/auth/types';
import { db } from '../../core/firebase/firebase';

type Role = 'super_admin' | 'admin' | 'manager' | 'staff';
type Department = 'flight' | 'package' | 'cruise' | 'account' | 'general' | 'visa' | 'insurance' | 'transport';

type StaffUser = {
  id: string;
  uid: string;
  email: string;
  fullName: string;
  role: Role;
  primaryDepartment: Department;
  departments: Department[];
  isActive: boolean;
  phone: string;
  canViewFinance: boolean;
  canEditBookings: boolean;
  canManageUsers: boolean;
  notes: string;
};

const roles: Role[] = ['super_admin', 'admin', 'manager', 'staff'];
const departments: Department[] = ['flight', 'package', 'cruise', 'account', 'general', 'visa', 'insurance', 'transport'];

function normalizeUser(id: string, raw: any): StaffUser {
  const primaryDepartment = (raw.primaryDepartment || raw.department || 'general') as Department;
  const depList = Array.isArray(raw.departments) && raw.departments.length ? raw.departments : [primaryDepartment];
  return {
    id,
    uid: String(raw.uid || id),
    email: String(raw.email || ''),
    fullName: String(raw.fullName || raw.name || raw.email || id),
    role: (raw.role || 'staff') as Role,
    primaryDepartment,
    departments: depList as Department[],
    isActive: raw.isActive !== false,
    phone: String(raw.phone || ''),
    canViewFinance: Boolean(raw.canViewFinance || raw.role === 'super_admin' || raw.role === 'admin'),
    canEditBookings: Boolean(raw.canEditBookings || raw.role === 'super_admin' || raw.role === 'admin'),
    canManageUsers: Boolean(raw.canManageUsers || raw.role === 'super_admin' || raw.role === 'admin'),
    notes: String(raw.notes || '')
  };
}

function blankUser(): Partial<StaffUser> {
  return {
    uid: '',
    email: '',
    fullName: '',
    role: 'staff',
    primaryDepartment: 'general',
    departments: ['general'],
    isActive: true,
    phone: '',
    canViewFinance: false,
    canEditBookings: false,
    canManageUsers: false,
    notes: ''
  };
}

export default function UsersPage({ user }: { user: ERPUser }) {
  const [rows, setRows] = useState<StaffUser[]>([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<StaffUser | null>(null);
  const [form, setForm] = useState<Partial<StaffUser>>(blankUser());
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const canManage = user.role === 'super_admin' || user.role === 'admin' || Boolean((user as any).canManageUsers);

  async function loadUsers() {
    const snap = await getDocs(collection(db, 'users'));
    setRows(snap.docs.map((d) => normalizeUser(d.id, d.data())));
  }

  useEffect(() => {
    loadUsers().catch((error) => setMessage(error instanceof Error ? error.message : 'Unable to load users.'));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((item) => !q || [
      item.fullName,
      item.email,
      item.uid,
      item.role,
      item.primaryDepartment,
      item.phone,
      item.isActive ? 'active' : 'inactive'
    ].join(' ').toLowerCase().includes(q));
  }, [rows, search]);

  function startAdd() {
    if (!canManage) {
      setMessage('Only super admin/admin can add users.');
      return;
    }
    setEditing(null);
    setForm(blankUser());
    setMessage('');
    setOpen(true);
  }

  function startEdit(row: StaffUser) {
    if (!canManage) {
      setMessage('Only super admin/admin can edit users.');
      return;
    }
    setEditing(row);
    setForm(row);
    setMessage('');
    setOpen(true);
  }

  function toggleDepartment(dep: Department) {
    const current = form.departments || [];
    const next = current.includes(dep) ? current.filter((item) => item !== dep) : [...current, dep];
    setForm({ ...form, departments: next.length ? next : [form.primaryDepartment || 'general'] });
  }

  async function saveUser() {
    if (!canManage) {
      setMessage('Only super admin/admin can save users.');
      return;
    }
    if (!form.uid?.trim()) {
      setMessage('Firebase Auth UID is required.');
      return;
    }
    if (!form.email?.trim()) {
      setMessage('Email is required.');
      return;
    }
    if (!form.fullName?.trim()) {
      setMessage('Full Name is required.');
      return;
    }

    setBusy(true);
    try {
      const primaryDepartment = (form.primaryDepartment || 'general') as Department;
      const departmentsList = form.departments?.length ? form.departments : [primaryDepartment];
      const payload = {
        uid: form.uid,
        email: form.email,
        fullName: form.fullName,
        role: form.role || 'staff',
        primaryDepartment,
        department: primaryDepartment,
        departments: departmentsList,
        isActive: form.isActive !== false,
        phone: form.phone || '',
        canViewFinance: Boolean(form.canViewFinance),
        canEditBookings: Boolean(form.canEditBookings),
        canManageUsers: Boolean(form.canManageUsers),
        notes: form.notes || '',
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      };

      const savePayload: any = { ...payload };

      if (!editing) {
        savePayload.createdAt = serverTimestamp();
        savePayload.createdBy = user.uid;
      }

      await setDoc(doc(db, 'users', form.uid), savePayload, { merge: true });

      setOpen(false);
      await loadUsers();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to save user.');
    } finally {
      setBusy(false);
    }
  }

  async function setActive(row: StaffUser, active: boolean) {
    if (!canManage) {
      setMessage('Only super admin/admin can activate/deactivate users.');
      return;
    }
    await updateDoc(doc(db, 'users', row.uid), {
      isActive: active,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    });
    await loadUsers();
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Staff Setup</p>
          <h1>Users / Staff Access</h1>
          <p>Create matching ERP user profiles for Firebase Auth users, assign role, department, and access flags.</p>
        </div>
        <button className="btn primary" title="Add staff user profile" onClick={startAdd}>
          <Plus size={18} /> Add User
        </button>
      </div>

      {message && <div className="error-box" style={{ marginBottom: 20 }}>{message}</div>}

      <section className="card" style={{ marginBottom: 20 }}>
        <h3>Important Setup Rule</h3>
        <p className="muted">
          First create staff login in Firebase Authentication. Then copy that UID here as ERP User UID.
          Firestore document ID must match Firebase Auth UID.
        </p>
      </section>

      <div className="toolbar">
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: 16, top: 18, color: '#66758a' }} />
          <input
            style={{ paddingLeft: 44 }}
            placeholder="Search name, email, role, department"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      <section className="card table-card" style={{ marginTop: 20 }}>
        <table>
          <thead>
            <tr>
              <th>User</th><th>Role</th><th>Department Access</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="empty">No users found.</td></tr>
            )}

            {filtered.map((row) => (
              <tr key={row.uid}>
                <td><strong>{row.fullName}</strong><br /><small>{row.email}</small><br /><small>{row.uid}</small></td>
                <td><span className="pill">{row.role}</span></td>
                <td><strong>{row.primaryDepartment}</strong><br /><small>{row.departments.join(', ')}</small></td>
                <td><span className="pill">{row.isActive ? 'Active' : 'Inactive'}</span></td>
                <td className="actions">
                  <button title="Edit user profile" onClick={() => startEdit(row)}><Edit size={16} /></button>
                  {row.isActive ? (
                    <button title="Deactivate user" onClick={() => setActive(row, false)}><UserX size={16} /></button>
                  ) : (
                    <button title="Activate user" onClick={() => setActive(row, true)}><UserCheck size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {open && (
        <div className="modal-backdrop">
          <div className="modal large">
            <div className="modal-head">
              <h2>{editing ? 'Edit User' : 'Add User'}</h2>
              <button className="icon-only" title="Close" onClick={() => setOpen(false)}>×</button>
            </div>

            <div className="form-section-title">Firebase + Staff Identity</div>
            <div className="form-grid">
              <label>Firebase Auth UID *
                <input value={form.uid || ''} readOnly={Boolean(editing)} onChange={(event) => setForm({ ...form, uid: event.target.value })} placeholder="Paste Firebase Authentication UID" />
              </label>
              <label>Email *
                <input value={form.email || ''} onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="staff@sardarpateltravel.com" />
              </label>
              <label>Full Name *
                <input value={form.fullName || ''} onChange={(event) => setForm({ ...form, fullName: event.target.value })} />
              </label>
              <label>Phone
                <input value={form.phone || ''} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
              </label>
              <label>Role
                <select value={form.role || 'staff'} onChange={(event) => setForm({ ...form, role: event.target.value as Role })}>
                  {roles.map((item) => <option key={item}>{item}</option>)}
                </select>
              </label>
              <label>Primary Department
                <select value={form.primaryDepartment || 'general'} onChange={(event) => setForm({ ...form, primaryDepartment: event.target.value as Department, departments: [event.target.value as Department] })}>
                  {departments.map((item) => <option key={item}>{item}</option>)}
                </select>
              </label>
              <label>Status
                <select value={form.isActive === false ? 'inactive' : 'active'} onChange={(event) => setForm({ ...form, isActive: event.target.value === 'active' })}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </label>

              <div className="span-3">
                <b>Allowed Departments</b>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                  {departments.map((dep) => (
                    <label key={dep} style={{ display: 'flex', gap: 8, alignItems: 'center', border: '1px solid #d9e2ec', borderRadius: 12, padding: '8px 12px' }}>
                      <input type="checkbox" checked={(form.departments || []).includes(dep)} onChange={() => toggleDepartment(dep)} />
                      {dep}
                    </label>
                  ))}
                </div>
              </div>

              <div className="span-3">
                <b>Access Flags</b>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                  <label><input type="checkbox" checked={Boolean(form.canViewFinance)} onChange={(event) => setForm({ ...form, canViewFinance: event.target.checked })} /> Can View Finance</label>
                  <label><input type="checkbox" checked={Boolean(form.canEditBookings)} onChange={(event) => setForm({ ...form, canEditBookings: event.target.checked })} /> Can Edit Bookings</label>
                  <label><input type="checkbox" checked={Boolean(form.canManageUsers)} onChange={(event) => setForm({ ...form, canManageUsers: event.target.checked })} /> Can Manage Users</label>
                </div>
              </div>

              <label className="span-3">Notes
                <textarea value={form.notes || ''} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
              </label>
            </div>

            <div className="modal-foot">
              <button className="btn" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn primary" disabled={busy} onClick={saveUser}>{busy ? 'Saving...' : 'Save User'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
